import { API_GET_SSO_TOKEN } from 'constants/api';

// Each requirement is validated separately so the error names the exact unmet rule. The
// special-character class is `[^a-zA-Z0-9]` (anything that is not a letter or digit) to mirror the
// Accounts Identity `RequireNonAlphanumeric` rule (`!char.IsLetterOrDigit`), which accepts `_`, `-`,
// and every other symbol — so any password valid for sign-in is also settable here. The prior
// `\W`-based class wrongly excluded `_` (a word character), rejecting valid passwords (DEV-5233).
export const PASSWORD_VALIDATION_RULES = {
  minLength: 8,
  minLengthError: 'Password must be at least 8 characters long',
  requirements: [
    { regexp: /[a-z]/, error: 'Password must include a lowercase letter' },
    { regexp: /[A-Z]/, error: 'Password must include an uppercase letter' },
    { regexp: /\d/, error: 'Password must include a number' },
    { regexp: /[^a-zA-Z0-9]/, error: 'Password must include a special character' },
  ],
};

export const ACCOUNTS_API_REFRESH_TOKEN = '/api/refresh-token';
export const TRUST_DEVICE_ROUTE = '/api/tokens/trust-device';
export const SIGN_OUT_DEVICE_ROUTE = '/api/devices/sign-out';
export const SIGN_IN_WITH_CREDENTIALS = '/api/signin/credentials';
export const SIGN_IN_WITH_PASSWORD_ROUTE = '/api/signin/password';
export const SIGN_IN_WITH_OTP_REQUEST_ROUTE = '/api/signin/request';
export const SIGN_IN_CREATE_REQUEST_ROUTE = '/api/signin/create-request';
export const SIGN_IN_PASSKEY_ROUTE = '/api/passkey/signin';
export const SIGN_METHOD_ROUTE = '/api/signin/method';
export const PASSKEY_ASSERTION_OPTIONS_ROUTE = '/api/passkey/assertion/options';
// Enrollment routes are [Authorize] (post-auth) — intentionally NOT in UNPROTECTED_ROUTES (they carry the token).
export const PASSKEY_REGISTER_OPTIONS_ROUTE = '/api/passkey/register/options';
export const PASSKEY_REGISTER_ROUTE = '/api/passkey/register';
// Passkey management (list + revoke) — post-auth, carry the token.
export const PASSKEY_LIST_ROUTE = '/api/passkey';
export const PASSKEY_DELETE_ROUTE = '/api/passkey';
// sessionStorage flag: a PasskeyWithUsername user signed in via the OTP fallback this session → after auth,
// the authed shell prompts them once to enroll a passkey on this device (DEV-3881 enroll-on-first-login).
export const PASSKEY_ENROLLMENT_PROMPT_FLAG = 'passkeyEnrollmentPrompt';
export const SSO_URL = '/Sso/url';
export const ADMIN_PANEL_CLIENT_ID = 'AdminPanel';
export const CLOSING_PORTAL_CLIENT_ID = 'ClosingPortal';

export const LOGIN_METHOD = {
  PASSWORD: 'Password',
  OTP: 'Otp',
  PASSWORD_PLUS_OTP: 'PasswordPlusOtp',
  PASSWORD_PLUS_OTP_REMEMBER_DEVICE: 'PasswordPlusOtpRememberDevice',
  PASSKEY_WITH_USERNAME: 'PasskeyWithUsername',
  SSO: 'Sso',
};

// DEV-4157: Constrained "activation" token for TPO invitees with no password yet. The backend
// mints a JWT carrying the TokenPurpose=activation claim instead of a full session. The FE detects
// it to show the mandatory set-password screen. The protected endpoint accepts only this token.
export const ACTIVATION_TOKEN_CLAIM = 'http://interfirst.com/identity/claims/TokenPurpose';
export const ACTIVATION_TOKEN_CLAIM_VALUE = 'activation';
// Singular "user" — matches Skynet.Accounts' UsersController ([Route("api")] +
// [HttpPost("user/set-initial-password")]). The previous plural "users" 404'd every call.
export const SET_INITIAL_PASSWORD_ROUTE = '/api/user/set-initial-password';

// The ClientId a sub-company invite's SignInRequest is created with (hardcoded on the backend —
// InviteSubCompanyUserCommandHandler / ResendSubCompanyUserInviteCommandHandler), NOT
// ADMIN_PANEL_CLIENT_ID. Confirming that request (activation-link flow) requires this exact value —
// SignInRequestModel.ThrowIfClientIdDoesNotMatch rejects a mismatch with a 400.
export const TPO_INVITE_CLIENT_ID = 'TPOSignup';

export const UNPROTECTED_ROUTES = [
  '/FeatureToggles',
  SIGN_IN_WITH_CREDENTIALS,
  SIGN_IN_WITH_PASSWORD_ROUTE,
  SIGN_IN_WITH_OTP_REQUEST_ROUTE,
  SIGN_IN_CREATE_REQUEST_ROUTE,
  SIGN_IN_PASSKEY_ROUTE,
  SIGN_METHOD_ROUTE,
  PASSKEY_ASSERTION_OPTIONS_ROUTE,
  SSO_URL,
  API_GET_SSO_TOKEN,
  // DEV-4157: The set-initial-password endpoint carries an explicit activation bearer token (not
  // from jwtManager). Listing it here prevents a 401 on this call from triggering auto-logout.
  SET_INITIAL_PASSWORD_ROUTE,
];

export const SING_IN_VIA_TYPE = {
  PHONE: 'ViaPhone',
  EMAIL: 'ViaEmail',
  MAGIC_LINK: 'ViaMagicLink',
  CROSS_CLIENT: 'ViaCrossClient',
  IMPERSONATION: 'ViaImpersonation',
};
